import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router";
import {
  getGameSystems,
  handlePaging,
} from "../../../../components/api/APIUtilities";
import { signout } from "../../../../components/user/BCPAuth";
import { AuthContext } from "../../../../components/user/BCPAuthProvider";
import {
  useProcessing,
  useSignout,
} from "../../../../components/user/BCPAuthReducers";
import { postNotification } from "../../../../components/utilities/Notifications";
import { filterAndSortEvents } from "../../../../components/utilities/SortFilterFunctions";
import { isAppMode } from "../../../../global/AppOverlay";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { mapData } from "../../../events/utils/event";
import EventsOrganizeDefault from "../../../organize/EventsOrganize/EventsOrganizeDefault";

const IS_BCP = getEnvironmentVariable("IS_BCP") === "true";
const searchLimit = 30;

export default function OrgDashEvents(props) {
  const {
    allEvents,
    setAllEvents,
    organization,
    organize,
    gameSystems,
    setGameSystems,
  } = props;

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const appMode = isAppMode();
  const { organizationId } = useParams();

  const [subLoading, setSubLoading] = useState(
    allEvents?.length ? false : true
  );
  const [filteredEvents, setFilteredEvents] = useState(null);

  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorEl3, setAnchorEl3] = useState(null);

  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isSearching, setIsSearching] = useState(null);

  const [includeOrg, setIncludeOrg] = useState(true);

  const [toggleMap, setToggleMap] = useState(true);
  const [activeEvents, setActiveEvents] = useState(null);

  const useSignoutReducer = useSignout();
  const useProcessingReducer = useProcessing();
  const isTO =
    authContext?.authState?.identity?.isAdmin ||
    authContext?.authState?.identity?.userId === organization.ownerId ||
    organization?.myStoreUser?.isTO
      ? true
      : false;

  const search = async () => {
    let _gameSystems = gameSystems;

    if (!_gameSystems) {
      _gameSystems = await getGameSystems({
        inclSystem: true,
      });
      setGameSystems(_gameSystems);
    }

    try {
      const gameSystemDict = {};
      _gameSystems.forEach((gameSystem) => {
        gameSystemDict[gameSystem.id] = gameSystem;
      });

      const queryStringParameters = {
        limit: searchLimit,
        sortAscending: false,
        gameStoreId: organization?.id || organizationId,
      };
      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        queryStringParameters.gameSystemId =
          getEnvironmentVariable("SINGLE_GAME_SYSTEM");
      } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
        queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
          "SINGLE_GAME_MANUFACTURER"
        );
      }

      let data = await handlePaging(
        "/events",
        queryStringParameters,
        nextKey,
        setNextKey
      );

      data = mapData(data, gameSystemDict, moment);

      setAllEvents(data);

      if (data.length !== searchLimit) {
        setHasMore(false);
      }

      setAllEvents(allEvents?.length ? [...allEvents, ...data] : data);
      setIsSearching(false);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        try {
          if (IS_BCP) {
            navigate("/logout");
          } else {
            await signout(useSignoutReducer, {
              setProcessing: useProcessingReducer,
              navigate: navigate,
            });
          }
        } catch (err) {
          postNotification("error", err);
        }
        // navigate("/login");
        return;
      } else {
        postNotification("error", err);
      }
    } finally {
      setSubLoading(false);
    }
  };

  const deleteEvent = (eventId) => {
    var eventArray = [];
    if (allEvents?.length) {
      allEvents.forEach((event) => {
        if (event.id !== eventId) {
          eventArray.push(event);
        }
      });
    }
    setAllEvents(eventArray);
  };

  // Filter and sort events
  useEffect(() => {
    if (allEvents?.length && filters.length) {
      const tempEvents = filterAndSortEvents(allEvents, filters);

      setFilteredEvents(tempEvents);
    } else if (!filters.length) {
      setFilteredEvents(null);
    }
    // eslint-disable-next-line
  }, [filters, allEvents]);

  useEffect(() => {
    const check = async () => {
      try {
        if (!allEvents) await search();
      } catch (err) {
        setAllEvents(null);

        if (err?.response?.status === 401 || err?.response?.status === 403) {
          try {
            if (IS_BCP) {
              navigate("/logout");
            } else {
              await signout(useSignoutReducer, {
                setProcessing: useProcessingReducer,
                navigate: navigate,
              });
            }
          } catch (err) {
            postNotification("error", err);
          }
          // navigate("/login");
          return;
        } else {
          postNotification("error", err);
        }
      } finally {
        setSubLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <EventsOrganizeDefault
      loading={subLoading}
      hasMore={hasMore}
      isSearching={isSearching}
      deleteEvent={deleteEvent}
      search={search}
      events={allEvents}
      authContext={authContext}
      includeOrg={includeOrg}
      setIncludeOrg={setIncludeOrg}
      appMode={appMode}
      filters={filters}
      setFilters={setFilters}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      anchorEl2={anchorEl2}
      setAnchorEl2={setAnchorEl2}
      anchorEl3={anchorEl3}
      setAnchorEl3={setAnchorEl3}
      filteredEvents={filteredEvents}
      setFilteredEvents={setFilteredEvents}
      gameSystems={gameSystems}
      embed
      title="Events"
      organize={Boolean(organize) && isTO}
      setToggleMap={setToggleMap}
      toggleMap={toggleMap}
      activeEvents={activeEvents}
      setActiveEvents={setActiveEvents}
    />
  );
}
